export const resolveBlockDragPosition = (input: {
  usesLocalWrapper: boolean;
  blockX: number;
  blockY: number;
  dragX: number;
  dragY: number;
  scale: number;
}) => {
  return {
    x: snapScenePixel(
      input.usesLocalWrapper ? input.dragX / input.scale : input.blockX + input.dragX / input.scale,
    ),
    y: snapScenePixel(
      input.usesLocalWrapper ? input.dragY / input.scale : input.blockY + input.dragY / input.scale,
    ),
  };
};

export const resolveBlockTransformFrame = (input: {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}) => {
  return {
    x: snapScenePixel(input.x / input.scale),
    y: snapScenePixel(input.y / input.scale),
    width: Math.max(8, snapScenePixel(input.width / input.scale)),
    height: Math.max(8, snapScenePixel(input.height / input.scale)),
  };
};

export const resolveSceneViewport = (input: {
  canvasWidth: number;
  canvasHeight: number;
  blocks: Array<{ x: number; y: number; width: number; height: number }>;
  scale: number;
  padding?: number;
}) => {
  const padding = input.padding ?? 64;
  const minX = Math.min(0, ...input.blocks.map((block) => block.x));
  const minY = Math.min(0, ...input.blocks.map((block) => block.y));
  const maxX = Math.max(input.canvasWidth, ...input.blocks.map((block) => block.x + block.width));
  const maxY = Math.max(input.canvasHeight, ...input.blocks.map((block) => block.y + block.height));

  return {
    width: Math.ceil((maxX - minX) * input.scale + padding * 2),
    height: Math.ceil((maxY - minY) * input.scale + padding * 2),
    canvasOffsetX: padding - minX * input.scale,
    canvasOffsetY: padding - minY * input.scale,
  };
};

export const resolveCenteredSceneViewport = (input: {
  canvasWidth: number;
  canvasHeight: number;
  workspaceWidth: number;
  workspaceHeight: number;
  selectedBlock?: { x: number; y: number; width: number; height: number } | null;
  padding?: number;
  minWorkspaceWidth?: number;
  minWorkspaceHeight?: number;
  selectedPadding?: number;
}) => {
  const padding = input.padding ?? 72;
  const selectedPadding = input.selectedPadding ?? 48;
  const width = Math.max(input.minWorkspaceWidth ?? 320, input.workspaceWidth);
  const height = Math.max(input.minWorkspaceHeight ?? 420, input.workspaceHeight);
  const canvasCenterX = input.canvasWidth / 2;
  const canvasCenterY = input.canvasHeight / 2;
  const selectedBlock = input.selectedBlock;

  const selectedMinX = selectedBlock ? selectedBlock.x - selectedPadding : 0;
  const selectedMinY = selectedBlock ? selectedBlock.y - selectedPadding : 0;
  const selectedMaxX = selectedBlock
    ? selectedBlock.x + selectedBlock.width + selectedPadding
    : input.canvasWidth;
  const selectedMaxY = selectedBlock
    ? selectedBlock.y + selectedBlock.height + selectedPadding
    : input.canvasHeight;
  const halfSceneWidth = Math.max(
    input.canvasWidth / 2,
    canvasCenterX - selectedMinX,
    selectedMaxX - canvasCenterX,
  );
  const halfSceneHeight = Math.max(
    input.canvasHeight / 2,
    canvasCenterY - selectedMinY,
    selectedMaxY - canvasCenterY,
  );
  const availableHalfWidth = Math.max(1, width / 2 - padding);
  const availableHalfHeight = Math.max(1, height / 2 - padding);
  const scale = Math.min(
    availableHalfWidth / halfSceneWidth,
    availableHalfHeight / halfSceneHeight,
  );
  const previewWidth = input.canvasWidth * scale;
  const previewHeight = input.canvasHeight * scale;

  return {
    width: Math.ceil(width),
    height: Math.ceil(height),
    scale,
    previewWidth: Math.round(previewWidth),
    previewHeight: Math.round(previewHeight),
    canvasOffsetX: Math.round(width / 2 - previewWidth / 2),
    canvasOffsetY: Math.round(height / 2 - previewHeight / 2),
  };
};

export type WorkspaceTransform = {
  x: number;
  y: number;
  zoom: number;
};

export type ImageBrushPoint = {
  x: number;
  y: number;
};

export type ImageBrushStroke = {
  id: string;
  points: ImageBrushPoint[];
};

export const WORKSPACE_MIN_ZOOM = 0.25;
export const WORKSPACE_MAX_ZOOM = 4;

export const clampWorkspaceZoom = (
  zoom: number,
  minZoom = WORKSPACE_MIN_ZOOM,
  maxZoom = WORKSPACE_MAX_ZOOM,
) => Math.min(maxZoom, Math.max(minZoom, zoom));

export const resolveWorkspaceFitTransform = (input: {
  viewportWidth: number;
  viewportHeight: number;
  stageWidth: number;
  stageHeight: number;
  padding?: number;
  minZoom?: number;
  maxZoom?: number;
}): WorkspaceTransform => {
  const padding = input.padding ?? 88;
  const availableWidth = Math.max(1, input.viewportWidth - padding * 2);
  const availableHeight = Math.max(1, input.viewportHeight - padding * 2);
  const zoom = clampWorkspaceZoom(
    Math.min(availableWidth / input.stageWidth, availableHeight / input.stageHeight),
    input.minZoom,
    input.maxZoom,
  );

  return {
    x: Math.round(input.viewportWidth / 2 - (input.stageWidth * zoom) / 2),
    y: Math.round(input.viewportHeight / 2 - (input.stageHeight * zoom) / 2),
    zoom,
  };
};

export const resolveWorkspaceCenterTransform = (input: {
  transform: WorkspaceTransform;
  viewportWidth: number;
  viewportHeight: number;
  stageWidth: number;
  stageHeight: number;
}): WorkspaceTransform => ({
  x: Math.round(input.viewportWidth / 2 - (input.stageWidth * input.transform.zoom) / 2),
  y: Math.round(input.viewportHeight / 2 - (input.stageHeight * input.transform.zoom) / 2),
  zoom: input.transform.zoom,
});

export const resolvePannedWorkspaceTransform = (
  transform: WorkspaceTransform,
  delta: { x: number; y: number },
): WorkspaceTransform => ({
  ...transform,
  x: transform.x + delta.x,
  y: transform.y + delta.y,
});

export const resolveWorkspacePointerFromClientPoint = (input: {
  clientX: number;
  clientY: number;
  rectLeft: number;
  rectTop: number;
}) => ({
  x: input.clientX - input.rectLeft,
  y: input.clientY - input.rectTop,
});

export const resolveBlockInteractionState = (input: {
  canUpdateBlock: boolean;
  isTransformSelected: boolean;
  isPanToolActive: boolean;
  isImageBrushToolActive: boolean;
}) => {
  const isWorkspaceToolActive = input.isPanToolActive || input.isImageBrushToolActive;

  return {
    canDrag: input.canUpdateBlock && input.isTransformSelected && !isWorkspaceToolActive,
    shouldCapturePointerEvents: !isWorkspaceToolActive,
  };
};

export const shouldUseBlockBoundsHitArea = (input: { type: string; shapeType?: string }) =>
  input.type === "shape" && input.shapeType === "line";

export type MarqueeRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export const resolveMarqueeRect = (
  anchor: { x: number; y: number },
  pointer: { x: number; y: number },
): MarqueeRect => ({
  x: Math.min(anchor.x, pointer.x),
  y: Math.min(anchor.y, pointer.y),
  width: Math.abs(pointer.x - anchor.x),
  height: Math.abs(pointer.y - anchor.y),
});

export const resolveMarqueeSelectedBlockIds = (input: {
  rect: MarqueeRect;
  blocks: Array<{
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    visible?: boolean;
  }>;
  canvasOffsetX: number;
  canvasOffsetY: number;
  scale: number;
}) =>
  input.blocks.flatMap((block) => {
    if (block.visible === false) {
      return [];
    }

    const blockRect = {
      x: input.canvasOffsetX + block.x * input.scale,
      y: input.canvasOffsetY + block.y * input.scale,
      width: block.width * input.scale,
      height: block.height * input.scale,
    };

    return rectsIntersect(input.rect, blockRect) ? [block.id] : [];
  });

const rectsIntersect = (a: MarqueeRect, b: MarqueeRect) =>
  a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;

export const resolveWorkspaceZoomTransform = (input: {
  transform: WorkspaceTransform;
  nextZoom: number;
  origin?: { x: number; y: number } | null;
  fallbackOrigin: { x: number; y: number };
  minZoom?: number;
  maxZoom?: number;
}): WorkspaceTransform => {
  const origin = input.origin ?? input.fallbackOrigin;
  const nextZoom = clampWorkspaceZoom(input.nextZoom, input.minZoom, input.maxZoom);
  const zoomRatio = nextZoom / input.transform.zoom;

  return {
    x: origin.x - (origin.x - input.transform.x) * zoomRatio,
    y: origin.y - (origin.y - input.transform.y) * zoomRatio,
    zoom: nextZoom,
  };
};

export const resolveWorkspaceWheelZoomFactor = (deltaY: number, step = 1.08) => {
  if (deltaY === 0) {
    return 1;
  }

  return deltaY > 0 ? 1 / step : step;
};

export const resolveImageBrushLocalPoint = (input: {
  localX: number;
  localY: number;
  scale: number;
  width: number;
  height: number;
}): ImageBrushPoint | null => {
  if (input.scale <= 0) {
    return null;
  }

  const x = input.localX / input.scale;
  const y = input.localY / input.scale;

  if (x < 0 || y < 0 || x > input.width || y > input.height) {
    return null;
  }

  return {
    x: snapScenePixel(x),
    y: snapScenePixel(y),
  };
};

export const appendImageBrushPoint = (
  stroke: ImageBrushStroke,
  point: ImageBrushPoint,
  minDistance = 1,
): ImageBrushStroke => {
  const previousPoint = stroke.points.at(-1);
  if (previousPoint) {
    const distance = Math.hypot(point.x - previousPoint.x, point.y - previousPoint.y);
    if (distance < minDistance) {
      return stroke;
    }
  }

  return {
    ...stroke,
    points: [...stroke.points, point],
  };
};

export const canSubmitImageBrushFeedback = (input: {
  prompt: string;
  strokes: ImageBrushStroke[];
}) => input.prompt.trim().length > 0 && input.strokes.some((stroke) => stroke.points.length > 1);

export const resolveImageBrushToolAvailability = (input: {
  targetBlockId: string | null;
  activeRegenerationBlockIds: ReadonlySet<string>;
  hasSubmitHandler: boolean;
}) => {
  const isRegenerating = Boolean(
    input.targetBlockId && input.activeRegenerationBlockIds.has(input.targetBlockId),
  );

  return {
    isRegenerating,
    canUseBrush: Boolean(input.targetBlockId && input.hasSubmitHandler && !isRegenerating),
  };
};

export const resolveScaledGroupChildFrame = (input: {
  x: number;
  y: number;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
}) => ({
  x: snapScenePixel(input.x * input.scaleX),
  y: snapScenePixel(input.y * input.scaleY),
  width: Math.max(8, snapScenePixel(input.width * input.scaleX)),
  height: Math.max(8, snapScenePixel(input.height * input.scaleY)),
});

export const snapScenePixel = (value: number) => Math.round(value);

export const resolveCommittedTextEdit = (input: { previousText: string; nextText: string }) => {
  const nextText = input.nextText;

  if (!nextText.trim() || nextText === input.previousText) {
    return null;
  }

  return nextText;
};

export type TextEditKeyAction = "cancel" | "commit" | "insert-newline" | "continue";

export const resolveTextEditKeyAction = (input: {
  key: string;
  shiftKey: boolean;
  isComposing?: boolean;
}): TextEditKeyAction => {
  if (input.isComposing) {
    return "continue";
  }

  if (input.key === "Escape") {
    return "cancel";
  }

  if (input.key === "Enter") {
    return input.shiftKey ? "insert-newline" : "commit";
  }

  return "continue";
};

export const insertTextAtSelection = (input: {
  value: string;
  selectionStart: number;
  selectionEnd: number;
  text: string;
}) => {
  const start = Math.max(0, Math.min(input.selectionStart, input.value.length));
  const end = Math.max(start, Math.min(input.selectionEnd, input.value.length));
  const value = `${input.value.slice(0, start)}${input.text}${input.value.slice(end)}`;
  const selectionStart = start + input.text.length;

  return {
    value,
    selectionStart,
    selectionEnd: selectionStart,
  };
};

export const resolveTextEditorOverlayFrame = (input: {
  absoluteX: number;
  absoluteY: number;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
}) => ({
  left: input.absoluteX,
  top: input.absoluteY,
  width: input.width * input.scaleX,
  height: input.height * input.scaleY,
  transform: `rotate(${input.rotation}deg)`,
  transformOrigin: "top left",
});

export type TextEditOverlayLine = {
  text: string;
  start: number;
  end: number;
  x: number;
  y: number;
  height: number;
};

type UnpositionedTextEditOverlayLine = Omit<TextEditOverlayLine, "x" | "y" | "height">;

export type TextEditOverlayRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export const resolveTextRenderHeight = (input: {
  text: string;
  width: number;
  height: number;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
  padding: number;
  allowOverflowHeight?: boolean;
  measureText: (value: string) => number;
}) => {
  if (input.allowOverflowHeight === false) {
    return input.height;
  }

  const contentWidth = Math.max(0, input.width - input.padding * 2);
  const lineHeightPx = input.fontSize * input.lineHeight;
  const lines = wrapTextEditOverlayLines({
    text: input.text,
    contentWidth,
    letterSpacing: input.letterSpacing,
    measureText: input.measureText,
  });
  const contentHeight = Math.max(lineHeightPx, lines.length * lineHeightPx) + input.padding * 2;

  return Math.max(input.height, Math.ceil(contentHeight));
};

export const resolveTextEditOverlayLayout = (input: {
  text: string;
  width: number;
  height: number;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
  padding: number;
  align: "left" | "center" | "right";
  verticalAlign: "top" | "middle" | "bottom";
  selectionStart: number;
  selectionEnd: number;
  measureText: (value: string) => number;
}) => {
  const contentWidth = Math.max(0, input.width - input.padding * 2);
  const lineHeightPx = input.fontSize * input.lineHeight;
  const wrappedLines = wrapTextEditOverlayLines({
    text: input.text,
    contentWidth,
    letterSpacing: input.letterSpacing,
    measureText: input.measureText,
  });
  const lines = wrappedLines.map((line) => {
    const measuredWidth = measureTextWithLetterSpacing(
      line.text,
      input.letterSpacing,
      input.measureText,
    );
    const extraX =
      input.align === "center"
        ? Math.max(0, (contentWidth - measuredWidth) / 2)
        : input.align === "right"
          ? Math.max(0, contentWidth - measuredWidth)
          : 0;

    return {
      ...line,
      x: input.padding + extraX,
      y: 0,
      height: lineHeightPx,
    };
  });
  const contentHeight = Math.max(lineHeightPx, lines.length * lineHeightPx);
  const offsetY =
    input.verticalAlign === "middle"
      ? Math.max(0, (input.height - input.padding * 2 - contentHeight) / 2)
      : input.verticalAlign === "bottom"
        ? Math.max(0, input.height - input.padding * 2 - contentHeight)
        : 0;
  const laidOutLines = lines.map((line, index) => ({
    ...line,
    y: input.padding + offsetY + index * lineHeightPx,
  }));
  const caretIndex = clampSelectionIndex(input.selectionEnd, input.text.length);
  const caret = resolveCaretRect({
    lines: laidOutLines,
    text: input.text,
    index: caretIndex,
    lineHeight: lineHeightPx,
    letterSpacing: input.letterSpacing,
    measureText: input.measureText,
  });
  const selectionRects = resolveSelectionRects({
    lines: laidOutLines,
    text: input.text,
    selectionStart: input.selectionStart,
    selectionEnd: input.selectionEnd,
    lineHeight: lineHeightPx,
    letterSpacing: input.letterSpacing,
    measureText: input.measureText,
  });

  return { caret, selectionRects };
};

const wrapTextEditOverlayLines = (input: {
  text: string;
  contentWidth: number;
  letterSpacing: number;
  measureText: (value: string) => number;
}): UnpositionedTextEditOverlayLine[] => {
  const paragraphs = input.text.split("\n");
  const lines: UnpositionedTextEditOverlayLine[] = [];
  let paragraphStart = 0;

  for (const paragraph of paragraphs) {
    if (!paragraph) {
      lines.push({ text: "", start: paragraphStart, end: paragraphStart });
      paragraphStart += 1;
      continue;
    }

    let lineStart = 0;

    while (lineStart < paragraph.length) {
      let bestEnd = lineStart + 1;

      while (bestEnd <= paragraph.length) {
        const candidate = paragraph.slice(lineStart, bestEnd);
        const candidateWidth = measureTextWithLetterSpacing(
          candidate,
          input.letterSpacing,
          input.measureText,
        );

        if (candidateWidth > input.contentWidth && bestEnd > lineStart + 1) {
          break;
        }

        bestEnd += 1;
      }

      const maxEnd = Math.min(paragraph.length, bestEnd - 1);
      const breakIndex = paragraph.lastIndexOf(" ", maxEnd);
      const lineEnd = breakIndex > lineStart && maxEnd < paragraph.length ? breakIndex : maxEnd;
      const nextLineStart = lineEnd === breakIndex ? lineEnd + 1 : lineEnd;

      lines.push({
        text: paragraph.slice(lineStart, lineEnd),
        start: paragraphStart + lineStart,
        end: paragraphStart + lineEnd,
      });
      lineStart = Math.max(nextLineStart, lineStart + 1);
    }

    paragraphStart += paragraph.length + 1;
  }

  return lines.length > 0 ? lines : [{ text: "", start: 0, end: 0 }];
};

const resolveCaretRect = (input: {
  lines: TextEditOverlayLine[];
  text: string;
  index: number;
  lineHeight: number;
  letterSpacing: number;
  measureText: (value: string) => number;
}): TextEditOverlayRect => {
  const line =
    input.lines.find(
      (candidate) => input.index >= candidate.start && input.index <= candidate.end,
    ) ?? input.lines.at(-1);

  if (!line) {
    return { x: 0, y: 0, width: 1, height: input.lineHeight };
  }

  const localIndex = Math.max(0, Math.min(input.index - line.start, line.text.length));
  const prefix = line.text.slice(0, localIndex);

  return {
    x: line.x + measureTextWithLetterSpacing(prefix, input.letterSpacing, input.measureText),
    y: line.y,
    width: 1,
    height: line.height,
  };
};

const resolveSelectionRects = (input: {
  lines: TextEditOverlayLine[];
  text: string;
  selectionStart: number;
  selectionEnd: number;
  lineHeight: number;
  letterSpacing: number;
  measureText: (value: string) => number;
}): TextEditOverlayRect[] => {
  const start = clampSelectionIndex(
    Math.min(input.selectionStart, input.selectionEnd),
    input.text.length,
  );
  const end = clampSelectionIndex(
    Math.max(input.selectionStart, input.selectionEnd),
    input.text.length,
  );

  if (start === end) {
    return [];
  }

  return input.lines
    .map((line) => {
      const rectStart = Math.max(start, line.start);
      const rectEnd = Math.min(end, line.end);

      if (rectStart > rectEnd || (rectStart === rectEnd && rectEnd !== line.end)) {
        return null;
      }

      const startPrefix = line.text.slice(0, rectStart - line.start);
      const selectedText = line.text.slice(rectStart - line.start, rectEnd - line.start);
      const x =
        line.x + measureTextWithLetterSpacing(startPrefix, input.letterSpacing, input.measureText);
      const width = Math.max(
        1,
        measureTextWithLetterSpacing(selectedText, input.letterSpacing, input.measureText),
      );

      return { x, y: line.y, width, height: line.height };
    })
    .filter((rect): rect is TextEditOverlayRect => Boolean(rect));
};

const measureTextWithLetterSpacing = (
  value: string,
  letterSpacing: number,
  measureText: (value: string) => number,
) => measureText(value) + Math.max(0, value.length - 1) * letterSpacing;

const clampSelectionIndex = (index: number, length: number) => Math.max(0, Math.min(index, length));

export const resolveTransformerBoundBox = <T extends { width: number; height: number }>(
  previousBox: T,
  nextBox: T,
  minSize = 8,
) => {
  if (Math.abs(nextBox.width) < minSize || Math.abs(nextBox.height) < minSize) {
    return previousBox;
  }

  return nextBox;
};
